import './RoasCompositionByRevenueStream.scss';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  FC,
  CSSProperties
} from 'react';
import { MdClose } from 'react-icons/md';
import {
  Bar,
  BarChart,
  Cell,
  DefaultZIndexes,
  LabelList,
  Pie,
  PieChart,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  YAxisTickContentProps,
  ZIndexLayer
} from 'recharts';
import * as R from 'ramda';
import { Modal } from 'react-bootstrap';
import { ChartContainer } from '@Components/ChartContainer';
import { WidgetContainer } from '@Components/WidgetContainer';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { DropdownToggleType } from '@Components/Dropdown/dropdown-constants';
import { Design, Placement, Size, StretchMode } from '@Components/designConstants';
import { GraphTableToggleButton } from '@Components/GraphTableToggleButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import {
  Brand10,
  Brand20,
  Brand30,
  Brand40,
  Brand50,
  Brand60,
  Brand70,
  Brand80,
  Brand90,
  Brand100,
  Neutral300,
  Neutral400,
  PRIMARY_VISUALIZATION_COLORS
} from '@src/utils/colors';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { abbreviateNumber, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { wrapTextWithHyphenation } from '@src/utils/canvasTextUtils';
import { RoasCompositionTable } from './RoasCompositionTable';
import { formatPercentRounded } from '../utils/hroasUtils';
import { CompositionRow } from '../hroas-types';

export interface RoasCompositionByRevenueStreamProps {
  groupBy: string;
  groupByOptions: { label: string; value: string }[];
  toolbarOutcomeVariable?: string;
  models: { id: string; label: string; appLabel: string; enabled: boolean; weight: number }[];
  compositionDataRevenue?: CompositionRow[];
  compositionDataRoas?: CompositionRow[];
  disabledPlatform?: boolean;
  renderOmitDarkInputsToggle: boolean;
  omitDarkInputs: boolean;
  setOmitDarkInputs: Dispatch<SetStateAction<boolean>>;
}

const ACTUAL_RELATIVE_OPTIONS = [{ value: 'Actual' }, { value: 'Relative' }];
const SORT_OPTIONS = [{ value: 'Abc' }, { value: 'High to low' }];
const HEADER_OUTCOME_OPTIONS = [{ value: 'ROAS' }, { value: 'Revenue' }];

const CHANNEL_BAR_HEIGHT = 24;
const CHANNEL_BAR_GAP = 16;
const OVERALL_BAR_HEIGHT = 40;
const OVERALL_SECTION_HEIGHT = 56;
const AXIS_WIDTH_BASE = 300;
const AXIS_WIDTH_WITH_RELATIVE_PCT = 330;
const TOTAL_X_END_WHEN_RELATIVE = 270;
const RELATIVE_PCT_X = 316;
const ROAS_DECIMAL_PLACES = 2;
const LABEL_LINE_HEIGHT = 16;
const TICK_LABEL_FONT = '500 14px Inter, -apple-system, sans-serif';
const TICK_X_OFFSET = 8;
const HOVER_RECT_RADIUS = 4;
const TOTAL_LABEL_RIGHT_MARGIN = 24;

const BRAND_COLORS_4 = [Brand80, Brand60, Brand40, Brand20];
const BRAND_COLORS_5 = [Brand90, Brand70, Brand50, Brand30, Brand10];

const getModelColors = (count: number): string[] => {
  if (count <= 4) {
    return BRAND_COLORS_4;
  }
  if (count === 5) {
    return BRAND_COLORS_5;
  }
  return [...PRIMARY_VISUALIZATION_COLORS];
};

const CURSOR_EXTEND_RIGHT = 3000;

const formatValue = (value: number, outcome: 'ROAS' | 'Revenue'): string =>
  outcome === 'ROAS' ? formatNumber(value, ROAS_DECIMAL_PLACES) : `$${abbreviateNumber(value, 1)}`;
const BAR_CORNER_RADIUS = 4;

const getBarRadius = (
  firstIdx: number,
  lastIdx: number,
  barIndex: number
): number | [number, number, number, number] => {
  if (firstIdx === barIndex && lastIdx === barIndex) {
    return BAR_CORNER_RADIUS;
  }
  if (firstIdx === barIndex) {
    return [BAR_CORNER_RADIUS, 0, 0, BAR_CORNER_RADIUS];
  }
  if (lastIdx === barIndex) {
    return [0, BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0];
  }
  return 0;
};

interface BarChartDataPoint {
  name: string;
  [modelId: string]: string | number;
}

interface SegmentLabelProps {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  height?: string | number;
  value?: string | number | boolean | null;
  name?: string;
  dataKey?: string;
  // recharts 3's LabelList `content` no longer passes `payload`; it does pass `index` (the row's
  // position in the Bar's data array). We inject `barData` at the call site and recover the row
  // from `barData[index]`.
  index?: number;
  barData?: BarChartDataPoint[];
}

const toBarChartData = (
  rows: CompositionRow[],
  modelIds: string[],
  actualVsRelative: 'Actual' | 'Relative'
): BarChartDataPoint[] =>
  rows.map(row => {
    const { valuesByModel } = row;
    const total = modelIds.reduce((sum, id) => sum + (valuesByModel[id] ?? 0), 0);
    const out: BarChartDataPoint = { name: row.label };
    modelIds.forEach(id => {
      const v = valuesByModel[id] ?? 0;
      out[id] = actualVsRelative === 'Relative' && total > 0 ? (100 * v) / total : v;
    });
    return out;
  });

const sortRows = (
  rows: CompositionRow[],
  sortBy: string,
  modelIds: string[],
  actualVsRelative: 'Actual' | 'Relative'
): CompositionRow[] => {
  const overall = rows.find(r => r.label === 'Overall');
  const rest = rows.filter(r => r.label !== 'Overall');
  const sorted =
    sortBy === 'High to low'
      ? R.sortBy(row => {
          if (actualVsRelative === 'Relative' && modelIds.length > 1) {
            const total = modelIds.reduce((s, id) => s + (row.valuesByModel[id] ?? 0), 0);
            return total > 0 ? -(row.valuesByModel[modelIds[0]] ?? 0) / total : 0;
          }
          return -modelIds.reduce((s, id) => s + (row.valuesByModel[id] ?? 0), 0);
        }, rest)
      : R.sortBy(R.compose(R.toLower, R.prop('label')), rest);
  return overall ? [...sorted, overall] : sorted;
};

export const RoasCompositionByRevenueStream: FC<RoasCompositionByRevenueStreamProps> = ({
  groupBy,
  groupByOptions,
  toolbarOutcomeVariable: _toolbarOutcomeVariable,
  models,
  compositionDataRevenue = [],
  compositionDataRoas = [],
  disabledPlatform: _disabledPlatform,
  renderOmitDarkInputsToggle,
  omitDarkInputs,
  setOmitDarkInputs
}) => {
  const [actualVsRelative, setActualVsRelative] = useState<'Actual' | 'Relative'>('Actual');
  const [sortBy, setSortBy] = useState<string>('High to low');
  const [showDonutModal, setShowDonutModal] = useState(false);
  const [selectedRowLabel, setSelectedRowLabel] = useState<string | null>(null);
  const [headerOutcome, setHeaderOutcome] = useState<'ROAS' | 'Revenue'>('ROAS');
  const [barLegendHiddenIds, setBarLegendHiddenIds] = useState<string[]>([]);
  const [activeRowLabel, setActiveRowLabel] = useState<string | null>(null);
  const [graphView, setGraphView] = useState(true);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const chartScrollRef = useRef<HTMLDivElement>(null);

  const enabledModels = useMemo(() => models.filter(m => m.enabled), [models]);
  const modelIds = useMemo(() => enabledModels.map(m => m.id), [enabledModels]);
  const visibleModelIdsForBar = useMemo(
    () => modelIds.filter(id => !barLegendHiddenIds.includes(id)),
    [modelIds, barLegendHiddenIds]
  );
  const axisWidth = useMemo(
    () => (visibleModelIdsForBar.length === 1 ? AXIS_WIDTH_WITH_RELATIVE_PCT : AXIS_WIDTH_BASE),
    [visibleModelIdsForBar.length]
  );
  const labelMaxWidth = useMemo(() => axisWidth - 120, [axisWidth]);
  const modelColorMap = useMemo(() => {
    const colors = getModelColors(enabledModels.length);
    const map: Record<string, string> = {};
    enabledModels.forEach((model, index) => {
      map[model.id] = colors[index % colors.length];
    });
    return map;
  }, [enabledModels]);

  const compositionData = useMemo(
    () => (headerOutcome === 'Revenue' ? compositionDataRevenue : compositionDataRoas),
    [headerOutcome, compositionDataRevenue, compositionDataRoas]
  );
  const sortedRows = useMemo(
    () => sortRows(compositionData, sortBy, visibleModelIdsForBar, actualVsRelative),
    [compositionData, sortBy, visibleModelIdsForBar, actualVsRelative]
  );
  const tableSortedRows = useMemo(
    () => sortRows(compositionDataRevenue, sortBy, visibleModelIdsForBar, actualVsRelative),
    [compositionDataRevenue, sortBy, visibleModelIdsForBar, actualVsRelative]
  );
  const barChartData = useMemo(
    () => toBarChartData(sortedRows, visibleModelIdsForBar, actualVsRelative),
    [sortedRows, visibleModelIdsForBar, actualVsRelative]
  );

  const channelBarChartData = useMemo(
    () => barChartData.filter(d => d.name !== 'Overall'),
    [barChartData]
  );

  const overallBarChartData = useMemo(
    () => barChartData.filter(d => d.name === 'Overall'),
    [barChartData]
  );

  useLayoutEffect(() => {
    const el = chartScrollRef.current;
    if (el) {
      setScrollbarWidth(el.offsetWidth - el.clientWidth);
    }
  }, [channelBarChartData]);

  const wrappedChannelLabels = useMemo(() => {
    const result: Record<string, string[]> = {};
    channelBarChartData.forEach(d => {
      // isTruncated intentionally unused — the SVG <title> on each tick already
      // provides the full label on hover.
      const { lines } = wrapTextWithHyphenation(String(d.name), labelMaxWidth, TICK_LABEL_FONT, 4);
      result[String(d.name)] = lines;
    });
    return result;
  }, [channelBarChartData, labelMaxWidth]);

  const maxChannelLabelLines = useMemo(
    () => Math.max(1, ...Object.values(wrappedChannelLabels).map(lines => lines.length)),
    [wrappedChannelLabels]
  );

  const dynamicChannelRowHeight = useMemo(() => {
    const textHeight = maxChannelLabelLines * LABEL_LINE_HEIGHT + 8;
    return Math.max(CHANNEL_BAR_HEIGHT + CHANNEL_BAR_GAP, textHeight);
  }, [maxChannelLabelLines]);

  const dynamicBarCategoryGap = useMemo(
    () => dynamicChannelRowHeight - CHANNEL_BAR_HEIGHT,
    [dynamicChannelRowHeight]
  );

  const xAxisDomain = useMemo((): [number, number | string] => {
    if (actualVsRelative === 'Relative') {
      return [0, 100];
    }
    const stackTotal = (row: BarChartDataPoint) =>
      visibleModelIdsForBar.reduce((s, id) => s + (Number(row[id]) || 0), 0);
    const maxChannelStack = channelBarChartData.reduce(
      (max, row) => Math.max(max, stackTotal(row)),
      0
    );
    return [0, maxChannelStack > 0 ? maxChannelStack : 'auto'];
  }, [actualVsRelative, channelBarChartData, visibleModelIdsForBar]);

  const channelChartContentHeight = useMemo(
    () =>
      channelBarChartData.length > 0
        ? channelBarChartData.length * dynamicChannelRowHeight + dynamicBarCategoryGap
        : 0,
    [channelBarChartData.length, dynamicChannelRowHeight, dynamicBarCategoryGap]
  );

  const MIN_SEGMENT_WIDTH_FOR_LABEL = 28;

  const renderSegmentLabel = useCallback(
    (props: SegmentLabelProps) => {
      // recharts 3 strips `payload` from LabelList content props but still passes `index`; recover
      // the row from the injected `barData` array, falling back to `name` for other paths.
      const idx = typeof props.index === 'number' ? props.index : undefined;
      const rowFromIndex = idx != null ? props.barData?.[idx] : undefined;
      const rowName = rowFromIndex?.name ?? props.name;
      const isOverall = rowName === 'Overall';
      if (!isOverall && (activeRowLabel == null || rowName !== activeRowLabel)) {
        return null;
      }
      if (actualVsRelative === 'Relative' && visibleModelIdsForBar.length === 1) {
        return null;
      }
      const { x: xVal, y: yVal, width: widthVal, height: heightVal, dataKey } = props;
      const x = Number(xVal ?? 0);
      const y = Number(yVal ?? 0);
      const width = Number(widthVal ?? 0);
      const height = Number(heightVal ?? 0);
      const row = rowFromIndex ?? barChartData.find(r => r.name === rowName);
      let rawValue: number;
      if (row != null && dataKey != null && row[dataKey] != null) {
        rawValue = Number(row[dataKey]);
      } else if (typeof props.value === 'number') {
        rawValue = props.value;
      } else {
        rawValue = Number(props.value);
      }
      if (rawValue === 0 || Number.isNaN(rawValue) || width < MIN_SEGMENT_WIDTH_FOR_LABEL) {
        return null;
      }
      const textX = x + width / 2;
      const textY = y + height / 2;
      const text =
        actualVsRelative === 'Relative'
          ? formatPercentRounded(rawValue)
          : formatValue(rawValue, headerOutcome);
      const modelLabel = enabledModels.find(m => m.id === dataKey)?.appLabel;
      const fullText = modelLabel ? `${text} ${modelLabel}` : text;
      const LABEL_PADDING = 4;
      const APPROX_CHAR_WIDTH = 7.5;
      let displayText: string;
      if (width >= fullText.length * APPROX_CHAR_WIDTH + LABEL_PADDING * 2) {
        displayText = fullText;
      } else if (width >= text.length * APPROX_CHAR_WIDTH + LABEL_PADDING * 2) {
        displayText = text;
      } else {
        return null;
      }
      const clipId = `seg-clip-${String(rowName)}-${String(dataKey)}`;
      return (
        <>
          <defs>
            <clipPath id={clipId}>
              <rect x={x + 4} y={y} width={Math.max(0, width - 8)} height={height} />
            </clipPath>
          </defs>
          <text
            x={textX}
            y={textY}
            textAnchor='middle'
            dominantBaseline='middle'
            fill='white'
            fontSize={13}
            fontWeight={500}
            clipPath={`url(#${clipId})`}
          >
            {displayText}
          </text>
        </>
      );
    },
    [
      actualVsRelative,
      activeRowLabel,
      barChartData,
      enabledModels,
      headerOutcome,
      visibleModelIdsForBar
    ]
  );

  const pngDownload = useCallback(async () => {
    await downloadPNG(
      '.roas-composition-by-revenue-stream .chart-container',
      'roas_composition_by_revenue_stream',
      '',
      [],
      true,
      true
    );
  }, []);

  const excelDownload = useCallback(() => {
    const flat = sortedRows.flatMap(row => {
      const total = modelIds.reduce((s, id) => s + (row.valuesByModel[id] ?? 0), 0);
      return modelIds.map(modelId => ({
        Row: row.label,
        Model: enabledModels.find(m => m.id === modelId)?.appLabel ?? modelId,
        Value: row.valuesByModel[modelId] ?? 0,
        'Row Total': total
      }));
    });
    exportToExcel(flat, 'ROAS_Composition_By_Revenue_Stream');
  }, [sortedRows, modelIds, enabledModels]);

  interface TooltipPayloadEntry {
    payload?: BarChartDataPoint;
    dataKey?: string;
    value?: number;
    color?: string;
  }

  const handleBarClick = useCallback((data: BarChartDataPoint) => {
    setSelectedRowLabel(data.name);
    setShowDonutModal(true);
  }, []);

  const RowHighlightCursor = useCallback(
    (props: {
      payload?: { payload?: BarChartDataPoint }[];
      y?: number;
      width?: number;
      height?: number;
    }) => {
      const { y = 0, width = 0, height = 0, payload } = props;
      const rowPayload = payload?.[0]?.payload;
      return (
        <rect
          className='roas-composition-row-highlight-cursor'
          x={axisWidth}
          y={y}
          width={width + CURSOR_EXTEND_RIGHT}
          height={height}
          fill={Neutral300}
          rx={HOVER_RECT_RADIUS}
          ry={HOVER_RECT_RADIUS}
          pointerEvents='auto'
          onClick={e => {
            e.stopPropagation();
            if (rowPayload?.name) {
              handleBarClick(rowPayload);
            }
          }}
        />
      );
    },
    [handleBarClick, axisWidth]
  );

  const handleBarLegendItemClick = useCallback(
    (modelId: string) => {
      setBarLegendHiddenIds(prev => {
        const isHidden = prev.includes(modelId);
        if (isHidden) {
          return prev.filter(id => id !== modelId);
        }
        if (visibleModelIdsForBar.length <= 1) {
          return prev;
        }
        return [...prev, modelId];
      });
    },
    [visibleModelIdsForBar.length]
  );

  const handleCloseModal = useCallback(() => {
    setShowDonutModal(false);
  }, []);

  const handleTableRowClick = useCallback((label: string) => {
    setSelectedRowLabel(label);
    setShowDonutModal(true);
  }, []);

  const showTotalInAxis = actualVsRelative === 'Actual';

  const visibleTotalByLabel = useMemo(() => {
    const m: Record<string, number> = {};
    sortedRows.forEach(r => {
      m[r.label] = visibleModelIdsForBar.reduce((s, id) => s + (r.valuesByModel[id] ?? 0), 0);
    });
    return m;
  }, [sortedRows, visibleModelIdsForBar]);

  const singleModelRelativePctByLabel = useMemo(() => {
    if (visibleModelIdsForBar.length !== 1) {
      return {};
    }
    const modelId = visibleModelIdsForBar[0];
    const channelRows = sortedRows.filter(r => r.label !== 'Overall');
    const total = channelRows.reduce((s, r) => s + (r.valuesByModel[modelId] ?? 0), 0);
    const m: Record<string, number> = {};
    channelRows.forEach(r => {
      const v = r.valuesByModel[modelId] ?? 0;
      m[r.label] = total > 0 ? (100 * v) / total : 0;
    });
    const overallRow = sortedRows.find(r => r.label === 'Overall');
    if (overallRow) {
      m.Overall = 100;
    }
    return m;
  }, [visibleModelIdsForBar, sortedRows]);

  const renderYAxisTick = useCallback(
    (barData: BarChartDataPoint[], rowHeight: number, wrappedLabels?: Record<string, string[]>) => {
      // eslint-disable-next-line react/no-unstable-nested-components -- recharts tick renderer closes over parent state (activeRowLabel, visibleTotalByLabel, handleBarClick); cannot be hoisted
      const YAxisTickContent = ({ y = 0, payload }: YAxisTickContentProps) => {
        const label = String(payload?.value ?? '');
        let total: number | null = null;
        if (showTotalInAxis) {
          const fromMap = visibleTotalByLabel[label];
          if (fromMap != null) {
            total = fromMap;
          } else {
            const row = barData.find(r => r.name === label);
            total = row
              ? visibleModelIdsForBar.reduce((s, id) => s + (Number(row[id]) || 0), 0)
              : null;
          }
        }
        const relativePct =
          visibleModelIdsForBar.length === 1 ? singleModelRelativePctByLabel[label] : undefined;
        const isActive = activeRowLabel === label;
        const lines = wrappedLabels?.[label] ?? [label];
        const startDy = -((lines.length - 1) * LABEL_LINE_HEIGHT) / 2;

        return (
          <g transform={`translate(${TICK_X_OFFSET},${y})`}>
            {isActive && (
              // The YAxis ticks are portaled to the axis layer (z=500), which sits ABOVE the bars
              // (z=300). Rendering the grey row-highlight inline here would cover the colored bar
              // segments. Portal it into the barBackground layer (z=-50) so it sits BEHIND the bars
              // while the LabelList value labels (z=2000) stay on top. ZIndexLayer portals into a
              // different layer's root <g> that does NOT carry this tick's translate transform, so
              // the inner <g> re-applies translate(TICK_X_OFFSET, y) to preserve the rect's position.
              <ZIndexLayer zIndex={DefaultZIndexes.barBackground}>
                <g transform={`translate(${TICK_X_OFFSET},${y})`}>
                  <rect
                    x={-TICK_X_OFFSET}
                    y={-rowHeight / 2}
                    width={axisWidth + CURSOR_EXTEND_RIGHT + TICK_X_OFFSET}
                    height={rowHeight}
                    fill={Neutral300}
                    rx={HOVER_RECT_RADIUS}
                    ry={HOVER_RECT_RADIUS}
                    pointerEvents='none'
                  />
                </g>
              </ZIndexLayer>
            )}
            <rect
              x={-TICK_X_OFFSET}
              y={-rowHeight / 2}
              width={axisWidth + TICK_X_OFFSET}
              height={rowHeight}
              fill='transparent'
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setActiveRowLabel(label)}
              onClick={() => {
                const row = barData.find(r => r.name === label);
                if (row) {
                  handleBarClick(row);
                }
              }}
            />
            <text x={0} fontSize={14} fontWeight={500} fill={Brand100} textAnchor='start'>
              <title>{label}</title>
              {lines.map((line, i) => (
                <tspan
                  key={i}
                  x={0}
                  dy={i === 0 ? startDy : LABEL_LINE_HEIGHT}
                  dominantBaseline='middle'
                >
                  {line}
                </tspan>
              ))}
            </text>
            {total != null && total > 0 && (
              <text
                x={
                  visibleModelIdsForBar.length === 1
                    ? TOTAL_X_END_WHEN_RELATIVE
                    : axisWidth - TOTAL_LABEL_RIGHT_MARGIN
                }
                y={0}
                fontSize={14}
                fontWeight={400}
                fill={Brand100}
                textAnchor='end'
                dominantBaseline='middle'
              >
                {formatValue(total, headerOutcome)}
              </text>
            )}
            {relativePct != null && visibleModelIdsForBar.length === 1 && label !== 'Overall' && (
              <text
                x={RELATIVE_PCT_X}
                y={0}
                fontSize={14}
                fontWeight={400}
                fill={Brand100}
                textAnchor='end'
                dominantBaseline='middle'
              >
                {formatPercentRounded(relativePct)}
              </text>
            )}
          </g>
        );
      };
      return YAxisTickContent;
    },
    [
      showTotalInAxis,
      visibleTotalByLabel,
      singleModelRelativePctByLabel,
      visibleModelIdsForBar,
      axisWidth,
      headerOutcome,
      activeRowLabel,
      handleBarClick,
      setActiveRowLabel
    ]
  );

  const selectedRow = useMemo(
    () => (selectedRowLabel ? (sortedRows.find(r => r.label === selectedRowLabel) ?? null) : null),
    [selectedRowLabel, sortedRows]
  );

  const donutPieData = useMemo(() => {
    if (!selectedRow) {
      return [];
    }
    return modelIds
      .map(id => ({
        name: id,
        value: selectedRow.valuesByModel[id] ?? 0
      }))
      .filter(entry => entry.value > 0);
  }, [selectedRow, modelIds]);

  const selectedRowTotal = useMemo(() => {
    if (!selectedRow) {
      return 0;
    }
    return modelIds.reduce((sum, id) => sum + (selectedRow.valuesByModel[id] ?? 0), 0);
  }, [selectedRow, modelIds]);

  const selectedRowDisplayTotal = selectedRowTotal;

  const donutLegendItems = useMemo(() => {
    if (!selectedRow) {
      return [];
    }
    const total = selectedRowTotal;
    return modelIds.map(id => {
      const rawValue = selectedRow.valuesByModel[id] ?? 0;
      const percentage = total > 0 ? (100 * rawValue) / total : 0;
      const modelLabel = enabledModels.find(m => m.id === id)?.appLabel ?? id;
      return {
        modelId: id,
        modelLabel,
        rawValue,
        percentage,
        color: modelColorMap[id]
      };
    });
  }, [selectedRow, modelIds, enabledModels, modelColorMap, selectedRowTotal]);

  const TooltipContentWithActive: FC<{
    payload?: TooltipPayloadEntry[];
  }> = ({ payload }) => {
    const rowName = payload?.[0]?.payload?.name;
    useEffect(() => {
      if (rowName) {
        setActiveRowLabel(rowName);
      }
    }, [rowName]);
    return null;
  };

  const widgetHeader = (
    <div className='roas-composition-widget-header'>
      <span>How did our investments drive </span>
      <Dropdown
        overlayPlacement={Placement.RIGHT}
        size={Size.LG}
        type={DropdownToggleType.WIDGET_TITLE}
        className='chart-title-dropdown'
        value={headerOutcome}
        options={HEADER_OUTCOME_OPTIONS}
        onChange={val => setHeaderOutcome(val as 'ROAS' | 'Revenue')}
      />
      <span> across various revenue streams?</span>
    </div>
  );

  if (enabledModels.length === 0 || compositionData.length === 0) {
    return (
      <WidgetContainer
        collapsible
        header={widgetHeader}
        subHeader={
          <>
            Allocation of channel {headerOutcome === 'Revenue' ? 'revenue' : headerOutcome} to
            component model revenue streams.{' '}
            <InfoTooltip rightLabel='More info' size={Size.SM}>
              {headerOutcome === 'Revenue'
                ? "Revenue is defined as the specific revenue attributed to the model feature within each underlying model. The chart stacks these individual model estimates to visualize the model feature's holistic revenue contribution."
                : "ROAS is calculated as (revenue attributed to the model feature within a specific underlying model) / (total media investment for that model feature). The chart stacks these individual model estimates to visualize the channel's holistic return."}
            </InfoTooltip>
          </>
        }
      >
        <ChartContainer title=''>
          <div className='roas-composition-placeholder'>No enabled models or data.</div>
        </ChartContainer>
      </WidgetContainer>
    );
  }

  return (
    <>
      <WidgetContainer
        collapsible
        header={widgetHeader}
        rightActions={
          renderOmitDarkInputsToggle ? (
            <ToggleSwitch
              design={Design.NEUTRAL}
              size={Size.LG}
              label='Omit Dark Inputs'
              checked={omitDarkInputs}
              onChange={() => {
                setOmitDarkInputs(current => !current);
              }}
              infoTooltipText='Dark inputs are defined as paid media features that account for <1% of spend in the trailing twelve months. Omitting these inputs allows for focus on active investments only.'
            />
          ) : undefined
        }
        subHeader={
          <>
            Allocation of channel {headerOutcome === 'Revenue' ? 'revenue' : headerOutcome} to
            component model revenue streams.{' '}
            <InfoTooltip rightLabel='More info' size={Size.SM}>
              {headerOutcome === 'Revenue'
                ? "Revenue is defined as the specific revenue attributed to the model feature within each underlying model. The chart stacks these individual model estimates to visualize the model feature's holistic revenue contribution."
                : "ROAS is calculated as (revenue attributed to the model feature within a specific underlying model) / (total media investment for that model feature). The chart stacks these individual model estimates to visualize the channel's holistic return."}
            </InfoTooltip>
          </>
        }
      >
        <div className='roas-composition-by-revenue-stream'>
          <ChartContainer
            title={
              <>
                {headerOutcome} Composition by Revenue Stream
                <span className='header-title-date-range'> | Trailing Twelve Months (TTM)</span>
              </>
            }
            rightActions={
              <>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.SM}
                  stretch={StretchMode.FIT}
                  value={actualVsRelative}
                  options={ACTUAL_RELATIVE_OPTIONS}
                  onChange={val => setActualVsRelative(val as 'Actual' | 'Relative')}
                />
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.SM}
                  stretch={StretchMode.FIT}
                  value={sortBy}
                  options={SORT_OPTIONS}
                  onChange={setSortBy}
                />
                <GraphTableToggleButton
                  graphView={graphView}
                  setGraphView={setGraphView}
                  widgetName='ROAS Composition by Revenue Stream'
                  design={Design.NEUTRAL}
                />
                <DownloadDropdown
                  size={Size.SM}
                  onClickOptions={[excelDownload, pngDownload]}
                  disabledMenuOptions={graphView ? undefined : { PNG: true }}
                />
              </>
            }
          >
            <div className='roas-composition-bar-view'>
              <div
                className={`roas-composition-chart-area${graphView ? '' : ' roas-composition-table-area'}`}
                onMouseLeave={() => setActiveRowLabel(null)}
              >
                {!graphView ? (
                  <RoasCompositionTable
                    sortedRows={tableSortedRows}
                    compositionDataRevenue={compositionDataRevenue}
                    compositionDataRoas={compositionDataRoas}
                    enabledModels={enabledModels}
                    visibleModelIds={visibleModelIdsForBar}
                    headerOutcome={headerOutcome}
                    actualVsRelative={actualVsRelative}
                    groupBy={groupByOptions.find(o => o.value === groupBy)?.label ?? groupBy}
                    onRowClick={handleTableRowClick}
                  />
                ) : (
                  <>
                    {visibleModelIdsForBar.length === 1 && (
                      <div className='roas-composition-column-headers'>
                        <span
                          className='roas-composition-column-header-actual'
                          style={{
                            visibility: actualVsRelative === 'Actual' ? 'visible' : 'hidden'
                          }}
                        >
                          {headerOutcome === 'Revenue' ? 'Revenue' : 'ROAS'}
                        </span>
                        <span className='roas-composition-column-header-relative'>
                          {headerOutcome === 'Revenue' ? '% of Total' : '% of Overall'}
                        </span>
                      </div>
                    )}
                    <div className='roas-composition-chart-scroll' ref={chartScrollRef}>
                      <div
                        className={`roas-composition-chart-scroll-content roas-composition-chart-scroll-content--rows-${Math.min(
                          channelBarChartData.length,
                          40
                        )}`}
                      >
                        {channelBarChartData.length > 0 && (
                          <ResponsiveContainer width='100%' height={channelChartContentHeight}>
                            <BarChart
                              data={channelBarChartData}
                              layout='vertical'
                              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                              barCategoryGap={dynamicBarCategoryGap}
                              barSize={CHANNEL_BAR_HEIGHT}
                            >
                              <XAxis type='number' hide domain={xAxisDomain} />
                              <YAxis
                                type='category'
                                dataKey='name'
                                width={axisWidth}
                                axisLine={false}
                                tickLine={false}
                                tick={renderYAxisTick(
                                  channelBarChartData,
                                  dynamicChannelRowHeight,
                                  wrappedChannelLabels
                                )}
                              />
                              <Tooltip
                                // eslint-disable-next-line react/no-unstable-nested-components -- recharts render prop forwarding to TooltipContentWithActive, which closes over setActiveRowLabel; cannot be hoisted
                                content={props => (
                                  <TooltipContentWithActive
                                    {...(props as unknown as {
                                      payload?: TooltipPayloadEntry[];
                                      active?: boolean;
                                    })}
                                  />
                                )}
                                // eslint-disable-next-line react-hooks/static-components -- RowHighlightCursor closes over axisWidth/handleBarClick; cannot be hoisted
                                cursor={<RowHighlightCursor />}
                              />
                              {visibleModelIdsForBar.map((modelId, barIndex) => (
                                <Bar
                                  key={modelId}
                                  dataKey={modelId}
                                  stackId='roas'
                                  isAnimationActive={false}
                                  // eslint-disable-next-line react/no-unstable-nested-components -- recharts Bar shape render prop closes over barIndex/visibleModelIdsForBar; cannot be hoisted
                                  shape={props => {
                                    const { payload: rowData } = props as {
                                      payload?: BarChartDataPoint;
                                    };
                                    const firstIdx = visibleModelIdsForBar.findIndex(
                                      id => ((rowData?.[id] as number) ?? 0) > 0
                                    );
                                    const lastIdx = visibleModelIdsForBar.reduce(
                                      (last, id, idx) =>
                                        ((rowData?.[id] as number) ?? 0) > 0 ? idx : last,
                                      -1
                                    );
                                    const dynamicRadius = getBarRadius(firstIdx, lastIdx, barIndex);
                                    return (
                                      <Rectangle
                                        {...(typeof props === 'object' && props && 'ref' in props
                                          ? { ...props, ref: undefined }
                                          : props)}
                                        radius={dynamicRadius}
                                      />
                                    );
                                  }}
                                  onClick={(data: unknown) => {
                                    const point = data as BarChartDataPoint;
                                    if (point?.name) {
                                      handleBarClick(point);
                                    }
                                  }}
                                >
                                  <LabelList
                                    content={props =>
                                      renderSegmentLabel({
                                        ...props,
                                        dataKey: modelId,
                                        barData: channelBarChartData
                                      })
                                    }
                                  />
                                  {channelBarChartData.map((entry, index) => (
                                    <Cell
                                      key={`${entry.name}-${modelId}-${index}`}
                                      fill={modelColorMap[modelId]}
                                    />
                                  ))}
                                </Bar>
                              ))}
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                    {overallBarChartData.length > 0 && (
                      <div
                        className='roas-composition-chart-overall'
                        // CSS custom property — only way to pass a runtime scrollbar measurement to SCSS
                        style={
                          {
                            '--overall-scroll-offset': `${scrollbarWidth + 8}px`
                          } as CSSProperties
                        }
                      >
                        <ResponsiveContainer width='100%' height={OVERALL_SECTION_HEIGHT}>
                          <BarChart
                            data={overallBarChartData}
                            layout='vertical'
                            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                            barCategoryGap={0}
                            barSize={OVERALL_BAR_HEIGHT}
                          >
                            <XAxis
                              type='number'
                              hide
                              domain={actualVsRelative === 'Relative' ? [0, 100] : [0, 'dataMax']}
                            />
                            <YAxis
                              type='category'
                              dataKey='name'
                              width={axisWidth}
                              axisLine={false}
                              tickLine={false}
                              tick={renderYAxisTick(overallBarChartData, OVERALL_SECTION_HEIGHT)}
                            />
                            <Tooltip
                              // eslint-disable-next-line react/no-unstable-nested-components -- recharts render prop forwarding to TooltipContentWithActive, which closes over setActiveRowLabel; cannot be hoisted
                              content={props => (
                                <TooltipContentWithActive
                                  {...(props as unknown as {
                                    payload?: TooltipPayloadEntry[];
                                    active?: boolean;
                                  })}
                                />
                              )}
                              // eslint-disable-next-line react-hooks/static-components -- RowHighlightCursor closes over axisWidth/handleBarClick; cannot be hoisted
                              cursor={<RowHighlightCursor />}
                            />
                            {visibleModelIdsForBar.map((modelId, barIndex) => (
                              <Bar
                                key={modelId}
                                dataKey={modelId}
                                stackId='roas'
                                isAnimationActive={false}
                                // eslint-disable-next-line react/no-unstable-nested-components -- recharts Bar shape render prop closes over barIndex/visibleModelIdsForBar; cannot be hoisted
                                shape={props => {
                                  const rowData = (props as unknown as Record<string, unknown>)
                                    .payload as BarChartDataPoint | undefined;
                                  const firstIdx = visibleModelIdsForBar.findIndex(
                                    id => ((rowData?.[id] as number) ?? 0) > 0
                                  );
                                  const lastIdx = visibleModelIdsForBar.reduce(
                                    (last, id, idx) =>
                                      ((rowData?.[id] as number) ?? 0) > 0 ? idx : last,
                                    -1
                                  );
                                  const dynamicRadius = getBarRadius(firstIdx, lastIdx, barIndex);
                                  return (
                                    <Rectangle
                                      {...(typeof props === 'object' && props && 'ref' in props
                                        ? { ...props, ref: undefined }
                                        : props)}
                                      radius={dynamicRadius}
                                    />
                                  );
                                }}
                                onClick={(data: unknown) => {
                                  const point = data as BarChartDataPoint;
                                  if (point?.name) {
                                    handleBarClick(point);
                                  }
                                }}
                              >
                                <LabelList
                                  content={props =>
                                    renderSegmentLabel({
                                      ...props,
                                      dataKey: modelId,
                                      barData: overallBarChartData
                                    })
                                  }
                                />
                                {overallBarChartData.map((entry, index) => (
                                  <Cell
                                    key={`${entry.name}-${modelId}-${index}`}
                                    fill={modelColorMap[modelId]}
                                  />
                                ))}
                              </Bar>
                            ))}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className='roas-composition-legend'>
                {enabledModels.map((model, _index) => {
                  const isHidden = barLegendHiddenIds.includes(model.id);
                  return (
                    <div
                      key={model.id}
                      role='button'
                      tabIndex={0}
                      className={`roas-composition-legend-item ${
                        isHidden ? 'roas-composition-legend-item-disabled' : ''
                      }`}
                      onClick={() => handleBarLegendItemClick(model.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleBarLegendItemClick(model.id);
                        }
                      }}
                    >
                      <div
                        className='roas-composition-legend-color'
                        style={{ backgroundColor: modelColorMap[model.id] }}
                      />
                      <span className='roas-composition-legend-label'>{model.appLabel}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </ChartContainer>
        </div>
      </WidgetContainer>
      <Modal
        show={showDonutModal}
        centered
        onHide={handleCloseModal}
        className='roas-composition-donut-modal'
      >
        <Modal.Header>
          <div className='roas-composition-modal-title'>
            {selectedRow?.label ?? ''} {headerOutcome} Distribution
          </div>
          <button type='button' className='roas-composition-modal-close' onClick={handleCloseModal}>
            <MdClose size={20} />
          </button>
        </Modal.Header>
        <Modal.Body>
          {selectedRow && (
            <div className='roas-composition-donut-content'>
              <div className='roas-composition-donut-chart-wrap' style={{ pointerEvents: 'none' }}>
                <ResponsiveContainer width={240} height={240}>
                  <PieChart>
                    <Pie
                      data={donutPieData}
                      dataKey='value'
                      nameKey='name'
                      isAnimationActive
                      animationDuration={1000}
                      animationEasing='ease-out'
                      cx='50%'
                      cy='50%'
                      innerRadius='30%'
                      outerRadius='100%'
                      startAngle={90}
                      endAngle={-270}
                    >
                      {donutPieData.map((entry, index) => (
                        <Cell
                          key={`donut-${entry.name}-${index}`}
                          fill={modelColorMap[entry.name] ?? Neutral400}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className='roas-composition-donut-legend'>
                <div className='roas-composition-donut-legend-title'>
                  {selectedRow.label}
                  <div className='roas-composition-donut-legend-subheader'>
                    {headerOutcome} Distribution by Revenue Stream
                  </div>
                </div>
                {donutLegendItems.map((item, _index) => (
                  <div key={item.modelId} className='roas-composition-donut-legend-row'>
                    <div className='roas-composition-donut-legend-key'>
                      <div
                        className='roas-composition-donut-legend-color'
                        style={{ backgroundColor: item.color }}
                      />
                      <span className='roas-composition-donut-legend-label'>{item.modelLabel}</span>
                    </div>
                    <div className='roas-composition-donut-legend-values'>
                      <span className='roas-composition-donut-legend-pct'>
                        {formatPercentRounded(item.percentage)}
                      </span>
                      <span className='roas-composition-donut-legend-value'>
                        {formatValue(item.rawValue, headerOutcome)}
                      </span>
                    </div>
                  </div>
                ))}
                <div className='roas-composition-donut-legend-overall'>
                  <span className='roas-composition-donut-legend-overall-label'>Overall</span>
                  <div className='roas-composition-donut-legend-values'>
                    <span className='roas-composition-donut-legend-pct roas-composition-donut-legend-pct-hidden'>
                      —
                    </span>
                    <span className='roas-composition-donut-legend-value'>
                      {actualVsRelative === 'Relative'
                        ? '100%'
                        : formatValue(selectedRowDisplayTotal, headerOutcome)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
